import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CoinsIcon } from '../assets/icons/CoinsIcon';
import { FiCheckCircle, FiLock, FiUnlock, FiZap } from 'react-icons/fi';
import '../styles/legal.scss';
import '../styles/how-payouts-work.scss';

const PLATFORM_FEE = 10;

const HowPayoutsWork: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="legal-page how-payouts">
      <div className="legal-page__container">

        <div className="legal-page__header">
          <div className="how-payouts__title-row">
            <CoinsIcon className="how-payouts__title-icon" />
            <h1 className="legal-page__title">How Payouts Work</h1>
          </div>
          <p className="legal-page__updated">
            Everything you need to know about earning through Philip Snat.
          </p>
        </div>

        <div className="how-payouts__overview-grid">
          <div className="how-payouts__overview-card">
            <FiZap size={18} className="how-payouts__overview-icon" />
            <span className="how-payouts__overview-label">Set your own price</span>
            <span className="how-payouts__overview-sub">You decide what each tier costs</span>
          </div>
          <div className="how-payouts__overview-card">
            <FiUnlock size={18} className="how-payouts__overview-icon" />
            <span className="how-payouts__overview-label">Direct to your bank</span>
            <span className="how-payouts__overview-sub">Stripe pays you directly</span>
          </div>
          <div className="how-payouts__overview-card">
            <FiLock size={18} className="how-payouts__overview-icon" />
            <span className="how-payouts__overview-label">30-day access windows</span>
            <span className="how-payouts__overview-sub">One charge, one month of access</span>
          </div>
        </div>

        <section className="legal-page__section">
          <h2 className="legal-page__section-title">Step 1 — Connect your Stripe account</h2>
          <p className="legal-page__text">
            Philip Snat uses <strong>Stripe Connect</strong> to handle all payments. Stripe is a globally
            trusted payment processor used by millions of businesses. When you connect your account, Stripe
            verifies your identity and links directly to your bank — we never hold your money.
          </p>
          <div className="legal-page__highlight">
            <p className="legal-page__text">
              <strong>How to connect:</strong> Go to your Expert Panel and click <em>Connect Stripe</em>.
              You'll be redirected to Stripe to complete a short onboarding flow (business type, bank details,
              identity verification). Once approved, you're ready to earn.
            </p>
          </div>
          <p className="legal-page__text">
            Stripe onboarding typically takes a few minutes. You may need to provide a government-issued ID
            and bank account details. This is a Stripe requirement — not ours — and it keeps everyone protected.
          </p>
        </section>

        <section className="legal-page__section">
          <h2 className="legal-page__section-title">Step 2 — Create paid tiers</h2>
          <p className="legal-page__text">
            Once Stripe is connected, you can create subscription tiers from the{' '}
            <button className="legal-page__link" type="button" onClick={() => navigate('/expert-panel/monetization')}>
              Manage Monetization
            </button>{' '}
            page. Each tier has:
          </p>
          <ul className="legal-page__list">
            <li><strong>A level</strong> — a number that determines tier rank (0 = free, higher = more exclusive).</li>
            <li><strong>A name</strong> — displayed to followers browsing your profile (e.g. "Bronze", "VIP", "Pro").</li>
            <li><strong>A monthly price</strong> — set in USD. This is the exact amount the follower will be charged.</li>
            <li><strong>An optional description</strong> — briefly explain what's included at this tier.</li>
          </ul>
          <p className="legal-page__text">
            Your <strong>free picks</strong> always remain visible to everyone. Paid tiers unlock access to
            picks you choose to restrict. You can have multiple tiers running simultaneously.
          </p>
        </section>

        <section className="legal-page__section">
          <h2 className="legal-page__section-title">How followers buy access</h2>
          <p className="legal-page__text">
            Followers visit your profile, choose a tier, and pay a one-time charge that grants them
            30 days of access to that tier's picks. There are no automatic renewals — when the 30 days
            expire, they can choose to buy again.
          </p>
          <div className="legal-page__highlight">
            <p className="legal-page__text">
              <strong>Important:</strong> Each purchase is a single, non-recurring payment. Followers are
              never auto-charged. This keeps things transparent for both sides.
            </p>
          </div>
        </section>

        <section className="legal-page__section">
          <h2 className="legal-page__section-title">Platform fee</h2>
          <p className="legal-page__text">
            Philip Snat charges a <strong>{PLATFORM_FEE}% platform fee</strong> on each transaction.
            This fee is deducted automatically before the remainder is transferred to your Stripe account.
          </p>
          <div className="legal-page__highlight">
            <p className="legal-page__text">
              <strong>Example:</strong> If a follower pays $20 for tier access, Philip Snat retains ${(20 * PLATFORM_FEE / 100).toFixed(2)} and you receive ${(20 * (1 - PLATFORM_FEE / 100)).toFixed(2)} — before Stripe's own processing fees.
            </p>
          </div>
          <p className="legal-page__text">
            Stripe also applies their standard card processing fees on top (see stripe.com for current rates). Both deductions are visible in your Stripe Dashboard per transaction.
          </p>
        </section>

        <section className="legal-page__section">
          <h2 className="legal-page__section-title">When and how you get paid</h2>
          <p className="legal-page__text">
            Every time a follower buys access to one of your tiers, Stripe processes the payment and
            routes your share directly to your connected bank account. Philip Snat does not act as an
            intermediary holding funds — the money goes from the follower's card to Stripe to your bank.
          </p>
          <ul className="legal-page__list">
            <li>Stripe typically pays out within <strong>2–7 business days</strong> depending on your bank and country.</li>
            <li>You can view your payout history and upcoming transfers inside your Stripe Dashboard at any time.</li>
            <li>Stripe's standard processing fees apply per transaction (visible in your Stripe Dashboard).</li>
          </ul>
        </section>

        <section className="legal-page__section">
          <h2 className="legal-page__section-title">What stays free</h2>
          <p className="legal-page__text">
            Monetization is entirely opt-in. Your free picks are always publicly visible — to followers
            and non-followers alike. Paid tiers are an optional layer on top. You decide which picks go
            behind which tier, or none at all.
          </p>
          <div className="how-payouts__checklist">
            <div className="how-payouts__check-item">
              <FiCheckCircle size={14} className="how-payouts__check-icon" />
              <span>Free picks are always visible to everyone</span>
            </div>
            <div className="how-payouts__check-item">
              <FiCheckCircle size={14} className="how-payouts__check-icon" />
              <span>You choose which picks to place behind a tier</span>
            </div>
            <div className="how-payouts__check-item">
              <FiCheckCircle size={14} className="how-payouts__check-icon" />
              <span>You can run multiple tiers at different price points</span>
            </div>
            <div className="how-payouts__check-item">
              <FiCheckCircle size={14} className="how-payouts__check-icon" />
              <span>Disconnecting Stripe freezes new subscriptions but doesn't remove existing access</span>
            </div>
          </div>
        </section>

        <section className="legal-page__section">
          <h2 className="legal-page__section-title">Need help?</h2>
          <p className="legal-page__text">
            If you have questions about your Stripe account, payouts, or tier setup — reach out via
            the contact details in the Terms of Service. For Stripe-specific payout issues, contact
            Stripe Support directly from your Stripe Dashboard.
          </p>
        </section>

      </div>
    </div>
  );
};

export default HowPayoutsWork;
